import { SAUDI_BRAND } from '../../products/saudi/config/saudiBrandConfig';

/** Canonical defaults — Arabic-first, aligned with catalog submodules. */
export const DEFAULT_ARTICLE_CONFIG = {
  // المنتجات والخدمات
  defaultUnit: 'قطعة',
  defaultTaxRate: SAUDI_BRAND.vatRate,
  requireCategory: false,
  requireBarcode: true,
  allowDuplicateBarcode: false,
  productReferencePrefix: 'PRD',
  serviceReferencePrefix: 'SRV',
  autoGenerateProductReference: true,
  autoGenerateServiceReference: true,

  // الفئات والمتغيرات
  categoryReferencePrefix: 'CAT',
  autoGenerateCategoryReference: true,
  enableVariants: true,
  variantReferencePrefix: 'VAR',
  autoGenerateVariantReference: true,

  // وحدات القياس
  uomReferencePrefix: 'UOM',
  autoGenerateUomReference: true,

  // طرق التكلفة وقوائم الأسعار
  enableCostMethods: true,
  costMethodReferencePrefix: 'CM',
  autoGenerateCostMethodReference: true,
  enablePriceLists: true,
  priceListReferencePrefix: 'PL',
  autoGeneratePriceListReference: true,
  defaultPriceIncludesVat: false,

  // سجل التغييرات والعرض
  enableChangelog: true,
  auditArticleChanges: true,
  itemsPerPage: 50,
  defaultView: 'table',
};

const FRENCH_UNIT_ALIASES = new Set(['unité', 'unite', 'Unité', 'piece', 'pcs']);

/** Map legacy API keys and scrub French leftovers. */
export function normalizeArticlesConfig(raw = {}) {
  const merged = { ...DEFAULT_ARTICLE_CONFIG, ...(raw && typeof raw === 'object' ? raw : {}) };

  if (FRENCH_UNIT_ALIASES.has(String(merged.defaultUnit || '').trim())) {
    merged.defaultUnit = DEFAULT_ARTICLE_CONFIG.defaultUnit;
  }

  if (raw.referencePrefix && !raw.productReferencePrefix) {
    merged.productReferencePrefix = raw.referencePrefix;
  }
  if (raw.autoGenerateReference != null && raw.autoGenerateProductReference == null) {
    merged.autoGenerateProductReference = raw.autoGenerateReference;
  }
  if (raw.trackCostMethods != null && raw.enableCostMethods == null) {
    merged.enableCostMethods = raw.trackCostMethods;
  }

  delete merged.referencePrefix;
  delete merged.autoGenerateReference;
  delete merged.trackCostMethods;
  delete merged.notifyPriceChange;
  delete merged.notifyNewProduct;
  delete merged._syncedFromApi;

  const pageSize = Number(merged.itemsPerPage);
  merged.itemsPerPage = [25, 50, 100].includes(pageSize)
    ? pageSize
    : DEFAULT_ARTICLE_CONFIG.itemsPerPage;

  if (merged.defaultView !== 'table' && merged.defaultView !== 'cards') {
    merged.defaultView = DEFAULT_ARTICLE_CONFIG.defaultView;
  }

  return merged;
}

export function getArticlesItemsPerPage(settingsOrConfig) {
  const raw = settingsOrConfig?.articles?.itemsPerPage ?? settingsOrConfig?.itemsPerPage;
  const n = Number(raw);
  return [25, 50, 100].includes(n) ? n : DEFAULT_ARTICLE_CONFIG.itemsPerPage;
}

export const ARTICLE_ENTITY_PREFIX_MAP = {
  product: 'productReferencePrefix',
  service: 'serviceReferencePrefix',
  category: 'categoryReferencePrefix',
  variant: 'variantReferencePrefix',
  unitOfMeasure: 'uomReferencePrefix',
  costMethod: 'costMethodReferencePrefix',
  priceList: 'priceListReferencePrefix',
};

export function buildArticlesEntityPrefixes(config) {
  const c = normalizeArticlesConfig(config);
  return {
    product: c.productReferencePrefix,
    service: c.serviceReferencePrefix,
    category: c.categoryReferencePrefix,
    variant: c.variantReferencePrefix,
    unitOfMeasure: c.uomReferencePrefix,
    costMethod: c.costMethodReferencePrefix,
    priceList: c.priceListReferencePrefix,
  };
}

export function getArticlesEntityPrefix(settings, entityKey, fallback) {
  const configKey = ARTICLE_ENTITY_PREFIX_MAP[entityKey];
  const fromArticles = configKey ? settings?.articles?.[configKey] : null;
  if (fromArticles) return String(fromArticles).trim() || fallback;
  return settings?.configuration?.entityPrefixes?.[entityKey] || fallback;
}

export function isArticlesAutoReferenceEnabled(settings, entityKey) {
  const map = {
    product: 'autoGenerateProductReference',
    service: 'autoGenerateServiceReference',
    category: 'autoGenerateCategoryReference',
    variant: 'autoGenerateVariantReference',
    unitOfMeasure: 'autoGenerateUomReference',
    costMethod: 'autoGenerateCostMethodReference',
    priceList: 'autoGeneratePriceListReference',
  };
  const key = map[entityKey];
  if (!key) return true;
  const val = settings?.articles?.[key];
  return val !== false;
}
